import React, { useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  Space,
  Select,
  Switch,
  Slider,
  InputNumber,
  Row,
  Col,
  Alert,
  Collapse,
  Typography,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import InlineButtonEditor, { InlineButtonLayout, inlineButtonsRule, subPagesButtonsRule } from './InlineButtonEditor';
import DynamicVariableEditor, { DynamicVariableConfig } from './DynamicVariableEditor';
import SubPagesEditor from './SubPagesEditor';
import TelegramChatPreview, { SubPage } from './TelegramChatPreview';
import MediaUploader from './MediaUploader';
import CustomEmojiPicker from './CustomEmojiPicker';

const { TextArea } = Input;
const { Text } = Typography;

interface AutoReply {
  id: number;
  bot_id: number | null;
  group_id: number | null;
  match_type: string;
  keyword: string;
  reply_text: string;
  reply_type: string;
  media_url: string | null;
  parse_mode: string;
  delete_trigger: number;
  reply_probability: number;
  permission_level: number;
  protect_content: number;
  inline_buttons: any;
  dynamic_variables: any;
  sub_pages: any;
  priority: number;
  status: number;
  description: string | null;
}

interface BotConfig {
  id: number;
  bot_name: string;
  bot_username: string;
}

interface Props {
  visible: boolean;
  editingRule: AutoReply | null;
  bots: BotConfig[];
  onSubmit: (values: any) => Promise<void>;
  onCancel: () => void;
}

const GroupAutoReplyEditModal: React.FC<Props> = ({
  visible,
  editingRule,
  bots,
  onSubmit,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [subPagesCollapseKey, setSubPagesCollapseKey] = React.useState<string[]>([]);
  const replyTextRef = React.useRef<any>(null);

  const handleEmojiInsert = React.useCallback((htmlTag: string) => {
    const textarea = replyTextRef.current?.resizableTextArea?.textArea as HTMLTextAreaElement | undefined;
    const currentVal = form.getFieldValue('replyText') || '';
    if (textarea) {
      const start = textarea.selectionStart ?? currentVal.length;
      const end = textarea.selectionEnd ?? currentVal.length;
      const newVal = currentVal.slice(0, start) + htmlTag + currentVal.slice(end);
      form.setFieldsValue({ replyText: newVal });
      // 恢复光标位置
      setTimeout(() => {
        textarea.focus();
        const pos = start + htmlTag.length;
        textarea.setSelectionRange(pos, pos);
      }, 0);
    } else {
      form.setFieldsValue({ replyText: currentVal + htmlTag });
    }
  }, [form]);

  useEffect(() => {
    if (!visible) return;
    if (editingRule) {
      let dynamicVariables: DynamicVariableConfig | null = null;
      if (editingRule.dynamic_variables) {
        try {
          dynamicVariables = typeof editingRule.dynamic_variables === 'string'
            ? JSON.parse(editingRule.dynamic_variables)
            : editingRule.dynamic_variables;
        } catch { dynamicVariables = null; }
      }

      let subPages: SubPage[] = [];
      if (editingRule.sub_pages) {
        try {
          subPages = typeof editingRule.sub_pages === 'string'
            ? JSON.parse(editingRule.sub_pages)
            : editingRule.sub_pages;
        } catch { subPages = []; }
      }

      // 如果已有子页面，默认展开折叠面板
      setSubPagesCollapseKey(subPages.length > 0 ? ['subpages'] : []);

      form.setFieldsValue({
        botId: editingRule.bot_id,
        groupId: editingRule.group_id,
        matchType: editingRule.match_type,
        keyword: editingRule.keyword,
        replyText: editingRule.reply_text,
        replyType: editingRule.reply_type,
        mediaUrl: editingRule.media_url,
        parseMode: editingRule.parse_mode,
        deleteTrigger: editingRule.delete_trigger === 1,
        replyProbability: editingRule.reply_probability,
        permissionLevel: editingRule.permission_level || 0,
        protectContent: editingRule.protect_content === 1,
        inlineButtons: (() => {
          if (!editingRule.inline_buttons) return [];
          if (Array.isArray(editingRule.inline_buttons)) return editingRule.inline_buttons;
          try { return JSON.parse(editingRule.inline_buttons); } catch { return []; }
        })(),
        dynamicVariables,
        subPages,
        priority: editingRule.priority,
        status: editingRule.status === 1,
        description: editingRule.description,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        matchType: 'contains',
        replyType: 'text',
        parseMode: 'HTML',
        deleteTrigger: false,
        replyProbability: 100,
        permissionLevel: 0,
        protectContent: true,
        inlineButtons: [],
        dynamicVariables: null,
        subPages: [],
        priority: 0,
        status: true,
      });
      setSubPagesCollapseKey([]);
    }
  }, [visible, editingRule, form]);

  return (
    <Modal
      title={editingRule ? '编辑自动回复规则' : '新建自动回复规则'}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={1100}
      style={{ top: 20 }}
      styles={{ body: { maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' } }}
    >
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Row gutter={24}>
          {/* 左侧：表单配置 */}
          <Col span={14}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="机器人" name="botId">
                  <Select placeholder="选择机器人（留空=全局）" allowClear>
                    {bots.map(bot => (
                      <Select.Option key={bot.id} value={bot.id}>
                        {bot.bot_name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="群组/频道ID" name="groupId" extra="填频道ID可对频道生效">
                  <InputNumber placeholder="留空=所有群组和频道" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="匹配类型"
                  name="matchType"
                  rules={[{ required: true, message: '请选择匹配类型' }]}
                >
                  <Select>
                    <Select.Option value="exact">精确匹配</Select.Option>
                    <Select.Option value="contains">包含</Select.Option>
                    <Select.Option value="regex">正则表达式</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="关键词"
                  name="keyword"
                  rules={[{ required: true, message: '请输入关键词' }]}
                >
                  <Input placeholder="触发关键词" />
                </Form.Item>
              </Col>
            </Row>

            <Alert
              message="支持的内置变量"
              description={
                <div style={{ fontSize: 12, lineHeight: '22px' }}>
                  <div style={{ marginBottom: 4, fontWeight: 500 }}>用户</div>
                  <div style={{ marginBottom: 6 }}>
                    <Text code>{'{user_id}'}</Text> 用户 Telegram ID &nbsp;
                    <Text code>{'{username}'}</Text> @用户名 &nbsp;
                    <Text code>{'{first_name}'}</Text> 名字 &nbsp;
                    <Text code>{'{last_name}'}</Text> 姓氏
                  </div>
                  <div style={{ marginBottom: 4, fontWeight: 500 }}>机器人</div>
                  <div style={{ marginBottom: 6 }}>
                    <Text code>{'{bot_id}'}</Text> 机器人 Telegram ID &nbsp;
                    <Text code>{'{bot_username}'}</Text> 机器人 @用户名 &nbsp;
                    <Text code>{'{bot_name}'}</Text> 机器人名称 &nbsp;
                    <Text code>{'{bot_invite_link}'}</Text> 机器人链接
                  </div>
                  <div style={{ marginBottom: 4, fontWeight: 500 }}>群组</div>
                  <div>
                    <Text code>{'{group_id}'}</Text> 群组 ID &nbsp;
                    <Text code>{'{group_title}'}</Text> 群组标题 &nbsp;
                    <Text code>{'{group_invite_link}'}</Text> 群组邀请链接
                  </div>
                </div>
              }
              type="info"
              icon={<InfoCircleOutlined />}
              style={{ marginBottom: 16 }}
            />

            {/* 回复类型 + 内容 */}
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="回复类型" name="replyType">
                  <Select>
                    <Select.Option value="text">文本</Select.Option>
                    <Select.Option value="photo">图片</Select.Option>
                    <Select.Option value="video">视频</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, curr) => prev.replyType !== curr.replyType}
                >
                  {({ getFieldValue }) => {
                    const rt = getFieldValue('replyType');
                    return rt !== 'text' ? (
                      <Form.Item label="媒体文件" name="mediaUrl">
                        <MediaUploader mediaType={rt === 'video' ? 'video' : 'photo'} />
                      </Form.Item>
                    ) : null;
                  }}
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) => prev.replyType !== curr.replyType}
            >
              {({ getFieldValue }) => {
                const rt = getFieldValue('replyType');
                const label = rt === 'text' ? '回复内容' : '图片/视频说明文字（Caption）';
                const placeholder = rt === 'text' ? '回复的文本内容，支持变量' : '显示在图片下方的说明文字（可留空）';
                return (
                  <Form.Item
                    label={
                      <Space>
                        <span>{label}</span>
                        <CustomEmojiPicker onInsert={handleEmojiInsert} buttonSize="small" />
                      </Space>
                    }
                    name="replyText"
                    rules={rt === 'text' ? [{ required: true, message: '请输入回复内容' }] : []}
                  >
                    <TextArea ref={replyTextRef} rows={4} placeholder={placeholder} />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="解析模式" name="parseMode">
                  <Select>
                    <Select.Option value="HTML">HTML</Select.Option>
                    <Select.Option value="Markdown">Markdown</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="删除触发消息" name="deleteTrigger" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="回复概率 (%)" name="replyProbability">
              <Slider min={0} max={100} marks={{ 0: '0%', 50: '50%', 100: '100%' }} />
            </Form.Item>

            {/* 动态变量 */}
            <Form.Item label="动态变量（外部API）" name="dynamicVariables">
              <DynamicVariableEditor />
            </Form.Item>

            {/* Inline 按钮（主页面） */}
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const dvConfig = getFieldValue('dynamicVariables');
                const dvFields = (dvConfig?.requests || [])
                  .flatMap((r: any) => r.response_fields || [])
                  .filter((f: string) => f.trim());
                const authCodeNames = (dvConfig?.auth_codes || [])
                  .map((c: any) => c.variable_name)
                  .filter(Boolean);
                const subPagesVal: SubPage[] = getFieldValue('subPages') || [];
                const allPageOptions = [
                  { pageIndex: 0, title: '主页面' },
                  ...subPagesVal.map(p => ({ pageIndex: p.pageIndex, title: p.title || `子页面 ${p.pageIndex}` })),
                ];
                return (
                  <Form.Item label="Inline 按钮（主页面）" name="inlineButtons" rules={[inlineButtonsRule]}>
                    <InlineButtonEditor
                      dynamicVarFields={dvFields}
                      authCodeVarNames={authCodeNames}
                      availablePages={allPageOptions}
                      currentPageIndex={0}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>

            {/* 子页面 */}
            <Collapse
              style={{ marginBottom: 16 }}
              activeKey={subPagesCollapseKey}
              onChange={(keys) => setSubPagesCollapseKey(typeof keys === 'string' ? [keys] : keys as string[])}
              items={[{
                key: 'subpages',
                label: (
                  <Space>
                    <Text strong>子页面（多级菜单）</Text>
                    <Form.Item noStyle shouldUpdate={(p, c) => p.subPages !== c.subPages}>
                      {({ getFieldValue }) => {
                        const cnt = (getFieldValue('subPages') || []).length;
                        return cnt > 0
                          ? <Text type="secondary" style={{ fontSize: 12 }}>{cnt} 个子页面</Text>
                          : <Text type="secondary" style={{ fontSize: 12 }}>未配置</Text>;
                      }}
                    </Form.Item>
                  </Space>
                ),
                children: (
                  <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                      const dvConfig = getFieldValue('dynamicVariables');
                      const dvFields = (dvConfig?.requests || [])
                        .flatMap((r: any) => r.response_fields || [])
                        .filter((f: string) => f.trim());
                      const authCodeNames = (dvConfig?.auth_codes || [])
                        .map((c: any) => c.variable_name)
                        .filter(Boolean);
                      return (
                        <Form.Item name="subPages" noStyle rules={[subPagesButtonsRule]}>
                          <SubPagesEditor dynamicVarFields={[...dvFields, ...authCodeNames]} />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                ),
              }]}
            />

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="触发权限" name="permissionLevel">
                  <Select>
                    <Select.Option value={0}>所有人</Select.Option>
                    <Select.Option value={1}>仅操作人</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="优先级" name="priority">
                  <InputNumber style={{ width: '100%' }} placeholder="数值越大优先级越高" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="禁止转发" name="protectContent" valuePropName="checked"
                  extra="开启后消息不可被转发/保存/复制">
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="启用" name="status" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="描述" name="description">
              <Input placeholder="规则说明（可选）" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  {editingRule ? '更新' : '创建'}
                </Button>
                <Button onClick={onCancel}>取消</Button>
              </Space>
            </Form.Item>
          </Col>

          {/* 右侧：实时预览 */}
          <Col span={10}>
            <div style={{ position: 'sticky', top: 0 }}>
              <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
                实时预览
              </Text>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const dvConfig = getFieldValue('dynamicVariables');
                  const dvFields = (dvConfig?.requests || [])
                    .flatMap((r: any) => r.response_fields || [])
                    .filter((f: string) => f.trim());
                  const subPages: SubPage[] = getFieldValue('subPages') || [];
                  return (
                    <TelegramChatPreview
                      triggerText={getFieldValue('keyword')}
                      replyText={getFieldValue('replyText')}
                      replyType={getFieldValue('replyType') || 'text'}
                      mediaUrl={getFieldValue('mediaUrl')}
                      parseMode={getFieldValue('parseMode') || 'HTML'}
                      buttons={getFieldValue('inlineButtons') || []}
                      dynamicVarFields={dvFields}
                      subPages={subPages}
                    />
                  );
                }}
              </Form.Item>
            </div>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default GroupAutoReplyEditModal;
